import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { connect } from 'react-redux';
import { fromJS } from 'immutable';
import { LOADING, ERROR } from '@client/definitions/constants';
import {
  selectDefaultDescriptionText,
  selectFundraiserByIdOrSlug,
  selectFundraiserStatus,
} from '@client/redux/fundraiser/fundraiserSelectors';
import { selectDonationList } from '@client/redux/donation';
import IndividualFundraiser from '@client/containers/individualFundraiser/IndividualFundraiser';
import styleHelper from '@client/shared/styles/styleHelper.scss';
import { getIn } from '@client/shared/utils/immutableUtils/immutableUtils';
import FundraiserCard from '@client/containers/fundraiserCard/FundraiserCard';
import ReactLink from '@client/components/reactLink/ReactLink';
import SimpleListing from '@client/components/listings/simpleListing/SimpleListing';
import MoreFromHlf from '@client/containers/moreFromHlf/MoreFromHlf';
import GenericHelmet from '@client/helperComponents/genericHelmet/GenericHelmet';
import { selectServerRequestInfo } from '@client/redux/requestState/requestStateReducer';
import { getImageOrDefault } from '@client/shared/utils/utils/utils';
import {
  loadIndividualFundraiserPageData,
  selectIndividualFundraiserPageData,
} from '@client/pages/individualFundraiserPage/duckIndividualFundraiserPage';
import FormFundraiserContainer from '@client/containers/formFundraiserContainer/FormFundraiserContainer';
import { selectEventBySlug } from '@client/redux/event/eventReducer';
import {
  getSearchParams,
  getSearchQueryValue,
} from '@client/shared/utils/urlUtils/urlUtils';
import { isMemorialFundraiser } from '@client/shared/utils/fundraiserUtils/fundraiserUtils';
import { ErrorBox } from '@client/components/errorBox/ErrorBox';
import Loader from '@client/components/loader/Loader';
import { BASENAME } from '../../../shared/constants/router';
import styles from './individualFundraiserPage.scss';

const stripBasename = (pathname) => {
  if (!pathname || !BASENAME) return pathname || '/';
  if (pathname === BASENAME) return '/';
  if (pathname.startsWith(`${BASENAME}/`)) {
    return pathname.slice(BASENAME.length);
  }
  return pathname;
};

class IndividualFundraiserPage extends PureComponent {
  static loadData(dispatch, urlLocation, matchParams) {
    const searchParams = getSearchParams(urlLocation);

    return dispatch(
      loadIndividualFundraiserPageData(matchParams.fundraiserSlug, searchParams)
    );
  }

  componentDidMount() {
    const { location, serverRequestInfo } = this.props;
    const initialRender =
      getIn(serverRequestInfo, 'pathname') === location?.pathname;
    if (!initialRender) {
      IndividualFundraiserPage.loadData(
        this.props.dispatch,
        this.props.location,
        this.props.match.params
      );
    }
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.match.params.fundraiserSlug !==
      prevProps.match.params.fundraiserSlug
    ) {
      IndividualFundraiserPage.loadData(
        this.props.dispatch,
        this.props.location,
        this.props.match.params
      );
    }
  }

  getShareUrl = () => {
    if (typeof window !== 'undefined' && window.location) {
      return `${window.location.origin}${stripBasename(
        window.location.pathname
      )}`;
    }
    const { serverRequestInfo } = this.props;
    const undecoratedHref = getIn(serverRequestInfo, 'undecoratedHref') || '';
    try {
      const parsed = new URL(undecoratedHref);
      return `${parsed.origin}${stripBasename(parsed.pathname)}`;
    } catch (_) {
      return undecoratedHref;
    }
  };

  getHelmetData = () => {
    const { fundraiser } = this.props;

    const undecoratedHref = this.getShareUrl();

    let metaRows = [
      {
        property: 'og:url',
        content: undecoratedHref,
      },
      {
        property: 'og:type',
        content: 'article',
      },
      {
        property: 'og:title',
        content: getIn(fundraiser, 'title'),
      },
      {
        property: 'og:description',
        content: getIn(fundraiser, 'description'),
      },
      {
        property: 'og:image',
        content: getImageOrDefault(getIn(fundraiser, 'imageUrl')),
      },
    ];

    let linkRows = [
      {
        rel: 'canonical',
        href: undecoratedHref,
      },
    ];

    const helmetData = fromJS({
      meta: metaRows,
      link: linkRows,
    });

    return helmetData;
  };

  render() {
    const {
      fundraiser,
      defaultDescriptionText,
      event,
      donations,
      pageData,
      location,
      status,
      serverRequestInfo,
    } = this.props;
    const serverPath = getIn(serverRequestInfo, 'pathname');
    const initialRender = serverPath === location?.pathname;

    if (status === LOADING && !initialRender) return <Loader />;
    if (status === ERROR) return <ErrorBox />;
    if (!fundraiser) return <Loader />;

    const otherFundraisers = getIn(fundraiser, 'otherFundraisers');

    const translationProps = fromJS({
      heroCollectedSoFar: getIn(pageData, 'heroCollectedSoFar'),
      buttonDonate: getIn(pageData, 'buttonDonate'),
      socialMediaBoxBeforeName: getIn(pageData, 'socialMediaBoxBeforeName'),
      socialMediaBoxAfterName: getIn(pageData, 'socialMediaBoxAfterName'),
      socialMediaBoxMemorialFund: getIn(pageData, 'socialMediaBoxMemorialFund'),
      socialMediaBoxTextUnknownOwner: getIn(
        pageData,
        'socialMediaBoxTextUnknownOwner'
      ),
      titleFundraiserDescription: getIn(pageData, 'titleFundraiserDescription'),
      titleMemorialFundDescription: getIn(
        pageData,
        'titleMemorialFundDescription'
      ),
      guestbookData: getIn(pageData, 'guestbook'),
      defaultDescriptionText: defaultDescriptionText,
      swishBoxText: getIn(pageData, 'swishBoxText'),
      swishBoxTitle: getIn(pageData, 'swishBoxTitle'),
    });

    const titleFundraiserListing = isMemorialFundraiser(
      getIn(fundraiser, 'fundraiserType')
    )
      ? getIn(pageData, 'titleMemorialFundListing')
      : getIn(pageData, 'titleIndependentFundraiserListing');

    const linkEndFundraiser =
      getIn(fundraiser, 'eventName') &&
      getIn(fundraiser, 'eventName') !== 'alla'
        ? fromJS({
            label: `Tillbaka till ${getIn(event, 'eventName')}`,
            linkUrl: `/event/${getIn(event, 'slug')}`,
          })
        : getIn(pageData, 'linkEndFundraiser', 'fields');

    const isEditMode = getSearchQueryValue(location?.search, 'edit') === 'true';
    const isPreviewMode =
      getSearchQueryValue(location?.search, 'preview') === 'true';
    const isPreviewOrEditMode = isPreviewMode || isEditMode;

    const wrap = (name, node) =>
      process.env.NODE_ENV === 'development' ? (
        <div
          data-hydration-marker={`ifp:${name}`}
          style={{ display: 'contents' }}
        >
          {node}
        </div>
      ) : (
        node
      );

    return (
      <div className={`${styles.individualFundraiserPage}`}>
        {isPreviewOrEditMode
          ? wrap(
              'form',
              <FormFundraiserContainer
                fundraiser={fundraiser}
                pageData={pageData}
                translations={translationProps}
              />
            )
          : wrap(
              'content',
              <div
                className={`${styleHelper.contentContainer} ${styles.individualFundraiserPageContent}`}
              >
                {wrap(
                  'individualFundraiser',
                  <>
                    <IndividualFundraiser
                      sharedUrl={this.getShareUrl()}
                      fundraiser={fundraiser}
                      donations={donations}
                      translations={translationProps}
                    />
                    {otherFundraisers && otherFundraisers.size > 0 ? (
                      <>
                        <h3
                          className={`${styleHelper.titleMdPrimary} ${styles.moreFundraisersTitle}`}
                        >
                          {titleFundraiserListing}
                        </h3>
                        <div className={styleHelper.alignmentContainer}>
                          <SimpleListing
                            className={styleHelper.cardListGrid}
                            list={otherFundraisers}
                            ListItemComponent={FundraiserCard}
                            listItemProps={{
                              itemObjName: 'fundraiser',
                              showStatsInsteadOfDescription: true,
                            }}
                          />
                        </div>
                      </>
                    ) : null}
                  </>
                )}
                {wrap(
                  'backLink',
                  <ReactLink
                    className={`${styleHelper.hlfLink} ${styles.seeMoreFundraiserLink}`}
                    to={getIn(linkEndFundraiser, 'linkUrl')}
                  >
                    {getIn(linkEndFundraiser, 'label')}
                  </ReactLink>
                )}
              </div>
            )}
        {wrap('moreFromHlf', <MoreFromHlf />)}
        {wrap('helmet', <GenericHelmet helmetData={this.getHelmetData()} />)}
      </div>
    );
  }
}

IndividualFundraiserPage.propTypes = {
  dispatch: PropTypes.func,
  text: PropTypes.string,
  location: PropTypes.object,
  match: PropTypes.object,
  fundraiser: ImmutablePropTypes.map,
  event: ImmutablePropTypes.map,
  donations: ImmutablePropTypes.list,
  pageData: ImmutablePropTypes.map,
  serverRequestInfo: ImmutablePropTypes.map,
  defaultDescriptionText: PropTypes.string,
  status: PropTypes.string,
};

function mapStateToProps(state, props) {
  const fundraiserSlug =
    props.match.params && props.match.params.fundraiserSlug;

  const fundraiser = selectFundraiserByIdOrSlug(
    state,
    fundraiserSlug.toString()
  );
  const fundraiserType = getIn(fundraiser, 'fundraiserType', 'name');

  return {
    fundraiser: fundraiser,
    event: selectEventBySlug(state, getIn(fundraiser, 'eventName')),
    donations: selectDonationList(state),
    serverRequestInfo: selectServerRequestInfo(state),
    pageData: selectIndividualFundraiserPageData(state),
    defaultDescriptionText: selectDefaultDescriptionText(state, fundraiserType),
    status: selectFundraiserStatus(state),
  };
}

export default connect(mapStateToProps)(IndividualFundraiserPage);
